import { useEffect, useRef, useState } from "react";
import type { DeliveryQrSource } from "@mnp/shared";

export interface RecentScan {
  qr: string;
  source: DeliveryQrSource;
}

interface ScannerCardProps {
  onScan: (qr: string, source: DeliveryQrSource) => void;
  recentScans?: RecentScan[];
  loading?: boolean;
}

// Security card personal-qr-hmac-enforce: the scanner path and the manual path
// are deliberately separate. Everything captured from the keyboard-wedge
// scanner (or typed into the field in scanner mode) goes to the backend with
// source=scan, where the daily HMAC token of the personal QR is strictly
// verified. Looking a client up by bare chatId requires the operator to
// explicitly switch to «Ввести вручную» (source=manual).
export function ScannerCard({ onScan, recentScans = [], loading = false }: ScannerCardProps) {
  const [mode, setMode] = useState<DeliveryQrSource>("scan");
  const [manualQr, setManualQr] = useState("");
  const bufferRef = useRef("");
  const timeoutRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function flushBuffer() {
      bufferRef.current = "";
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") return;

      if (event.key === "Enter") {
        const value = bufferRef.current.trim();
        flushBuffer();
        // HID keyboard-wedge input is always a scan, regardless of the UI mode.
        if (value) onScan(value, "scan");
        return;
      }

      if (event.key.length === 1) {
        bufferRef.current += event.key;
        if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(flushBuffer, 120);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      flushBuffer();
    };
  }, [onScan]);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const value = manualQr.trim();
    if (!value) return;
    setManualQr("");
    onScan(value, mode);
    inputRef.current?.focus();
  }

  function toggleMode() {
    setMode((prev) => (prev === "scan" ? "manual" : "scan"));
    inputRef.current?.focus();
  }

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4" aria-label="Сканер выдачи">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Сканер выдачи</h2>
          <p className="text-sm text-gray-500">
            {mode === "scan"
              ? "Отсканируйте личный QR клиента."
              : "Введите номер клиента (chatId) вручную."}
          </p>
        </div>
        {loading && <span className="text-sm text-mnp-stamp">Поиск…</span>}
      </div>
      <form onSubmit={submit} className="flex gap-2">
        <input
          ref={inputRef}
          value={manualQr}
          onChange={(event) => setManualQr(event.target.value)}
          disabled={loading}
          placeholder={mode === "scan" ? "QR клиента" : "Номер клиента (chatId)"}
          className="input-admin flex-1"
        />
        <button type="submit" className="btn-primary" disabled={loading || manualQr.trim().length === 0}>
          Найти
        </button>
      </form>
      <button
        type="button"
        onClick={toggleMode}
        className="mt-2 text-xs text-mnp-stamp underline-offset-2 hover:underline"
      >
        {mode === "scan" ? "Ввести вручную" : "Режим сканера"}
      </button>
      {recentScans.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Недавние QR</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {recentScans.map((recent) => (
              <button
                key={`${recent.source}:${recent.qr}`}
                type="button"
                onClick={() => onScan(recent.qr, recent.source)}
                className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:border-mnp-stamp"
              >
                {recent.qr}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
